import { useParams, useSearchParams } from 'react-router-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRef, useState, Fragment, useMemo, useEffect } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { ArrowBack, Download } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getStudentDetails } from '../../api/studentsApi';
import { useCenterProfile } from '../../hooks/useCenterProfile';

const ViewAdmitCardPage = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const year = searchParams.get('year');
  const semester = searchParams.get('semester');
  const admitCardRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Get user role from localStorage to determine navigation and API call
  const userRole = localStorage.getItem('userRole') || 'admin';
  const isCenterPanel = location.pathname.includes('/center/');
  
  const { data: studentResponse, isLoading: isLoadingStudent, error } = useQuery({
    queryKey: ['student', studentId, year, semester, userRole],
    queryFn: () => getStudentDetails(studentId || '', year || undefined, semester || undefined, userRole),
    enabled: !!studentId,
  });

  const student = studentResponse?.student || studentResponse?.data?.student;

  // Handle center details - centerId might be a populated object or just an ID string
  const centerIdValue = (student as any)?.centerId;
  const isCenterPopulated = centerIdValue && typeof centerIdValue === 'object' && centerIdValue.centerDetails;
  const centerIdString = isCenterPopulated ? null : (typeof centerIdValue === 'string' ? centerIdValue : null);
  
  // Only fetch center details if centerId is a string (not already populated)
  const { data: centerResponse } = useCenterProfile(centerIdString);
  const fetchedCenter = centerResponse?.data;
  
  // Use populated center from student object if available, otherwise use fetched center
  const center = isCenterPopulated ? centerIdValue : fetchedCenter;

  // Get subjects directly from student response (already filtered by year/semester by backend)
  const studentSubjects = (student as any)?.subjects || [];

  // Get approved admit card years/semesters
  const approvedYears: string[] = (student as any)?.approvedAdmitCardYears || [];
  const approvedSemesters: string[] = (student as any)?.approvedAdmitCardSemesters || [];

  // Determine if course is year-based or semester-based based on approved admit card terms
  const isYearBased = approvedYears.length > 0;

  // Get available terms (years or semesters) from approved list
  const availableTerms = isYearBased ? approvedYears : approvedSemesters;

  // Get current selected term
  const currentTerm = year || semester || (availableTerms.length > 0 ? availableTerms[0] : '');

  // Auto-select first term if no year/semester in URL and terms are available
  useEffect(() => {
    if (!year && !semester && availableTerms.length > 0 && studentId) {
      const firstTerm = availableTerms[0];
      const newSearchParams = new URLSearchParams(searchParams);
      if (isYearBased) {
        newSearchParams.set('year', firstTerm);
        newSearchParams.delete('semester');
      } else {
        newSearchParams.set('semester', firstTerm);
        newSearchParams.delete('year');
      }
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [year, semester, availableTerms, isYearBased, studentId, searchParams, setSearchParams]);

  // Handle term selection change
  const handleTermChange = (term: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (isYearBased) {
      newSearchParams.set('year', term);
      newSearchParams.delete('semester');
    } else {
      newSearchParams.set('semester', term);
      newSearchParams.delete('year');
    }
    setSearchParams(newSearchParams);
  };

  // Map subjects to display format with codes
  const subjectsWithCodes = useMemo(() => {
    if (!studentSubjects || studentSubjects.length === 0) return [];

    // Map subjects from student response to display format
    const mappedSubjects = studentSubjects.map((sub: any) => ({
      subjectName: sub.name || '',
      subjectCode: sub.code || '',
    }));

    // Sort by subject code (numeric part) in ascending order
    return mappedSubjects.sort((a: any, b: any) => {
      const codeA = a.subjectCode ? parseInt(a.subjectCode.replace(/\D/g, '')) || 0 : 0;
      const codeB = b.subjectCode ? parseInt(b.subjectCode.replace(/\D/g, '')) || 0 : 0;
      return codeA - codeB;
    });
  }, [studentSubjects]);

  const isLoading = isLoadingStudent;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Determine back navigation path based on role
  const getBackPath = () => {
    if (isCenterPanel) {
      return '/center/students';
    }
    return '/admin/students';
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading admit card: {error instanceof Error ? error.message : 'Student not found'}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(getBackPath())}
          sx={{ mt: 2 }}
        >
          Back to Students
        </Button>
      </Box>
    );
  }

  if (!student) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No student found.</Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(getBackPath())}
          sx={{ mt: 2 }}
        >
          Back to Students
        </Button>
      </Box>
    );
  }

  const handleDownload = async () => {
    if (admitCardRef.current && student) {
      setIsDownloading(true);
      
      // Declare variables outside try block so they're accessible in catch block
      let originalWidth: string = '';
      let originalHeight: string = '';
      let originalMinWidth: string = '';
      let originalMinHeight: string = '';
      let originalMaxWidth: string = '';
      let originalMaxHeight: string = '';
      let originalMargin: string = '';
      
      try {
        // Wait for all images to load before capturing
        const images = admitCardRef.current.querySelectorAll('img');
        await Promise.all(
          Array.from(images).map((img) => {
            return new Promise((resolve) => {
              if (img.complete && img.naturalWidth > 0) {
                resolve(true);
              } else {
                img.onload = () => resolve(true);
                img.onerror = () => resolve(true);
              }
            });
          })
        );

        // Wait for everything to render at natural size first
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Get the actual rendered dimensions (this matches what user sees in preview)
        const actualWidth = admitCardRef.current.offsetWidth;
        const actualHeight = admitCardRef.current.offsetHeight;
        
        // Store original styles to restore later
        originalWidth = admitCardRef.current.style.width;
        originalHeight = admitCardRef.current.style.height;
        originalMinWidth = admitCardRef.current.style.minWidth;
        originalMinHeight = admitCardRef.current.style.minHeight;
        originalMaxWidth = admitCardRef.current.style.maxWidth;
        originalMaxHeight = admitCardRef.current.style.maxHeight;
        originalMargin = admitCardRef.current.style.margin;
        
        // Lock the container to its current rendered size to prevent any shifts
        admitCardRef.current.style.setProperty('width', `${actualWidth}px`, 'important');
        admitCardRef.current.style.setProperty('height', `${actualHeight}px`, 'important');
        admitCardRef.current.style.setProperty('min-width', `${actualWidth}px`, 'important');
        admitCardRef.current.style.setProperty('min-height', `${actualHeight}px`, 'important');
        admitCardRef.current.style.setProperty('max-width', `${actualWidth}px`, 'important');
        admitCardRef.current.style.setProperty('max-height', `${actualHeight}px`, 'important');
        admitCardRef.current.style.setProperty('margin', '0', 'important');
        
        // Force reflow to ensure styles are applied
        admitCardRef.current.offsetHeight;
        
        // Additional wait to ensure everything is stable
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Use actual rendered dimensions for PDF
        const width = actualWidth;
        const height = actualHeight;
        
        // Use optimized scale (4-5) for high quality with reasonable file size
        const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
        // Scale of 4-5 provides ~384-480 DPI equivalent - excellent quality with manageable file size
        const scale = Math.min(Math.max(dpr * 4, 4), 5);
        
        const canvas = await html2canvas(admitCardRef.current, {
          backgroundColor: '#ffffff',
          scale: scale,
          useCORS: true,
          allowTaint: true,
          logging: false,
          removeContainer: false,
          imageTimeout: 25000,
          onclone: (clonedDoc) => {
            // Fix container dimensions in cloned document to match actual rendered size
            // Find the admit card container by data attribute
            const clonedContainer = clonedDoc.querySelector('[data-admit-card]') as HTMLElement;
            
            if (clonedContainer) {
              // Lock dimensions to match the actual rendered size
              clonedContainer.style.setProperty('width', `${width}px`, 'important');
              clonedContainer.style.setProperty('height', `${height}px`, 'important');
              clonedContainer.style.setProperty('min-width', `${width}px`, 'important');
              clonedContainer.style.setProperty('min-height', `${height}px`, 'important');
              clonedContainer.style.setProperty('max-width', `${width}px`, 'important');
              clonedContainer.style.setProperty('max-height', `${height}px`, 'important');
              clonedContainer.style.setProperty('margin', '0', 'important');
              clonedContainer.style.setProperty('box-sizing', 'border-box', 'important');
              clonedContainer.style.setProperty('position', 'relative', 'important');
              clonedContainer.style.setProperty('overflow', 'hidden', 'important');
              
              // Preserve all absolute positioned elements exactly as they are
              // Use the cloned document's window for computed styles
              const clonedWindow = clonedDoc.defaultView || window;
              const absoluteElements = clonedContainer.querySelectorAll('[style*="position: absolute"], [style*="position:absolute"], [style*="left"], [style*="top"]');
              absoluteElements.forEach((el) => {
                if (el instanceof HTMLElement) {
                  // Preserve existing position values from cloned document
                  const computedStyle = clonedWindow.getComputedStyle(el);
                  if (computedStyle.position === 'absolute') {
                    el.style.setProperty('position', 'absolute', 'important');
                    // Preserve left and top values if they exist
                    const left = computedStyle.left;
                    const top = computedStyle.top;
                    if (left && left !== 'auto') {
                      el.style.setProperty('left', left, 'important');
                    }
                    if (top && top !== 'auto') {
                      el.style.setProperty('top', top, 'important');
                    }
                  }
                }
              });
              
              // Ensure SVG image maintains its original size and positioning
              const svgImage = clonedContainer.querySelector('img[src*="mivps-admit-card"]') as HTMLImageElement;
              if (svgImage) {
                svgImage.style.setProperty('width', '100%', 'important');
                svgImage.style.setProperty('height', '100%', 'important');
                svgImage.style.setProperty('object-fit', 'contain', 'important');
                svgImage.style.setProperty('position', 'absolute', 'important');
                svgImage.style.setProperty('top', '0', 'important');
                svgImage.style.setProperty('left', '0', 'important');
              }
            }
            
            // Optimize rendering in cloned document for maximum quality
            const body = clonedDoc.body;
            if (body && body instanceof HTMLElement) {
              body.style.setProperty('-webkit-font-smoothing', 'antialiased');
              body.style.setProperty('text-rendering', 'optimizeLegibility');
              body.style.setProperty('image-rendering', 'crisp-edges');
            }
            // Optimize all text elements
            const textElements = clonedDoc.querySelectorAll('*');
            textElements.forEach((el) => {
              if (el instanceof HTMLElement) {
                el.style.setProperty('-webkit-font-smoothing', 'antialiased');
                el.style.setProperty('text-rendering', 'optimizeLegibility');
                el.style.setProperty('font-smooth', 'always');
              }
            });
            // Ensure SVG images are rendered at maximum quality
            const images = clonedDoc.querySelectorAll('img');
            images.forEach((img) => {
              if (img instanceof HTMLImageElement) {
                img.style.setProperty('image-rendering', 'crisp-edges');
                // For the admit card SVG, ensure it maintains full size
                if (img.src && img.src.includes('mivps-admit-card')) {
                  // Size is already set above, just ensure rendering quality
                  img.style.setProperty('image-rendering', 'crisp-edges', 'important');
                } else if (img.src && img.src.endsWith('.svg')) {
                  // For other SVGs, maintain their natural size
                  img.style.width = 'auto';
                  img.style.height = 'auto';
                  img.style.maxWidth = 'none';
                  img.style.maxHeight = 'none';
                }
              }
            });
          },
          foreignObjectRendering: false,
        });
        
        // Validate canvas
        if (!canvas || canvas.width === 0 || canvas.height === 0) {
          throw new Error('Canvas is empty or invalid');
        }
        
        // Use JPEG with maximum quality (0.99) for excellent quality with much smaller file size
        // At scale 4-5, JPEG 0.99 provides near-lossless quality while reducing file size by 80-90%
        const imgData = canvas.toDataURL('image/jpeg', 0.99);
        
        // Validate image data
        if (!imgData || imgData === 'data:,') {
          throw new Error('Failed to generate image data');
        }
        
        // Create PDF with the actual dimensions
        const pdf = new jsPDF({
          orientation: width > height ? 'landscape' : 'portrait',
          unit: 'px',
          format: [width, height],
          compress: false,
        });
        
        // Add high-resolution image - JPEG format with 0.99 quality provides excellent visual quality
        // with dramatically smaller file size compared to PNG
        pdf.addImage(imgData, 'JPEG', 0, 0, width, height);
        
        const termLabel = isYearBased ? `Year-${year || availableTerms[0]}` : `Sem-${semester || availableTerms[0]}`;
        const fileName = `admit-card-${student.registrationNo}-${student.candidateName.replace(/\s+/g, '-')}-${termLabel}.pdf`;
        pdf.save(fileName);
        
        // Restore original styles
        if (originalWidth) admitCardRef.current.style.width = originalWidth;
        else admitCardRef.current.style.removeProperty('width');
        if (originalHeight) admitCardRef.current.style.height = originalHeight;
        else admitCardRef.current.style.removeProperty('height');
        if (originalMinWidth) admitCardRef.current.style.minWidth = originalMinWidth;
        else admitCardRef.current.style.removeProperty('min-width');
        if (originalMinHeight) admitCardRef.current.style.minHeight = originalMinHeight;
        else admitCardRef.current.style.removeProperty('min-height');
        if (originalMaxWidth) admitCardRef.current.style.maxWidth = originalMaxWidth;
        else admitCardRef.current.style.removeProperty('max-width');
        if (originalMaxHeight) admitCardRef.current.style.maxHeight = originalMaxHeight;
        else admitCardRef.current.style.removeProperty('max-height');
        if (originalMargin) admitCardRef.current.style.margin = originalMargin;
        else admitCardRef.current.style.removeProperty('margin');
      } catch (error) {
        console.error('Error generating admit card PDF:', error);
        alert('Error generating admit card PDF. Please try again.');
        // Restore original styles even on error
        if (admitCardRef.current) {
          if (originalWidth) admitCardRef.current.style.width = originalWidth;
          else admitCardRef.current.style.removeProperty('width');
          if (originalHeight) admitCardRef.current.style.height = originalHeight;
          else admitCardRef.current.style.removeProperty('height');
          if (originalMinWidth) admitCardRef.current.style.minWidth = originalMinWidth;
          else admitCardRef.current.style.removeProperty('min-width');
          if (originalMinHeight) admitCardRef.current.style.minHeight = originalMinHeight;
          else admitCardRef.current.style.removeProperty('min-height');
          if (originalMaxWidth) admitCardRef.current.style.maxWidth = originalMaxWidth;
          else admitCardRef.current.style.removeProperty('max-width');
          if (originalMaxHeight) admitCardRef.current.style.maxHeight = originalMaxHeight;
          else admitCardRef.current.style.removeProperty('max-height');
          if (originalMargin) admitCardRef.current.style.margin = originalMargin;
          else admitCardRef.current.style.removeProperty('margin');
        }
      } finally {
        setIsDownloading(false);
      }
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(getBackPath())}
        >
          Back to Students
        </Button>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Year/Semester Selector */}
          {availableTerms.length > 1 && (
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Select {isYearBased ? 'Year' : 'Semester'}</InputLabel>
              <Select
                value={currentTerm}
                label={`Select ${isYearBased ? 'Year' : 'Semester'}`}
                onChange={(e) => handleTermChange(e.target.value)}
              >
                {availableTerms.map((term: string) => (
                  <MenuItem key={term} value={term}>
                    {isYearBased ? 'Year' : 'Semester'} {term}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          {/* Show current term if only one available */}
          {availableTerms.length === 1 && (
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
              {isYearBased ? 'Year' : 'Semester'} {availableTerms[0]} Admit Card
            </Typography>
          )}
        
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownload}
            disabled={isDownloading}
            sx={{
              backgroundColor: '#10b981',
              '&:hover': { backgroundColor: '#059669' },
            }}
          >
            {isDownloading ? 'Generating PDF...' : 'Download Admit Card (PDF)'}
          </Button>
        </Box>
      </Box>

      <Paper 
        ref={admitCardRef}
        data-admit-card="true"
        sx={{ 
          p: 4, 
          textAlign: 'center', 
          minHeight: '800px', 
          width: { xs: '100%', md: '1056px' },
          height: { xs: 'auto', md: '1494px' },
          aspectRatio: { md: '1056/1494' },
          display: 'flex', 
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          margin: '0 auto',
        }}
      >
        {/* SVG Background */}
        <Box
          component="img"
          src="/mivps-admit-card.svg"
          alt="Admit Card Template"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 0,
            objectFit: 'contain',
          }}
        />
        
        {/* Student Details Overlay - Adjust positions based on actual SVG template */}
        <Box sx={{ position: 'relative', height: '100%', zIndex: 1 }}>
          {/* Example fields - adjust positions based on actual SVG template */}
          <Box sx={{ position: 'absolute', left: '230px', top: '414px' }}>
            <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '1.2rem' }}>
              {student.candidateName}
            </Typography>
          </Box>
          
          <Box sx={{ position: 'absolute', left: '230px', top: '467px' }}>
            <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '1.2rem' }}>
              {student.fatherName}
            </Typography>
          </Box>
          
          <Box sx={{ position: 'absolute', left: '240px', top: '519px' }}>
            <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '1.2rem' }}>
              {student.motherName}
            </Typography>
          </Box>
          
          <Box sx={{ position: 'absolute', left: '213px', top: '569px' }}>
            <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '1.3rem' }}>
              {student.dateOfBirth}
            </Typography>
          </Box>
          
          {/* Exam Centre Address */}
          <Box sx={{ position: 'absolute', left: '265px', top: '652px', maxWidth: '700px' }}>
            <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '1.3rem' }}>
              {(() => {
                // First, try to get center address from fetched center details
                if (center?.centerDetails?.centerName) {
                  const centerDetails = center.centerDetails;
                  return centerDetails.centerName;
                }
                return 'N/A';
              })()}
            </Typography>
          </Box>
          {/* Course Name */}
          <Box sx={{ position: 'absolute', left: '265px', top: '752px', maxWidth: '700px' }}>
            <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '1.3rem' }}>
              {(() => {
                const course = (student as any)?.course;
                if (typeof course === 'object' && course?.name) {
                  return course.name;
                }
                return (student as any)?.course || 'N/A';
              })()}
            </Typography>
          </Box>
          
          {/* Roll No. */}
          <Box sx={{ position: 'absolute', right: '138px', top: '338px' }}>
            <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '1.4rem' }}>
              {student.registrationNo || 'N/A'}
            </Typography>
          </Box>
          
          {/* Sem/Year */}
          <Box sx={{ position: 'absolute', left: '205px', top: '337px' }}>
            <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '1.4rem' }}>
              {isYearBased ? `Year-${currentTerm}` : `Semester-${currentTerm}`}
            </Typography>
          </Box>
          
          {/* Student Photo */}
          {student.photo && (
            <Box
              sx={{
                position: 'absolute',
                left: '776.5px',
                top: '403px',
                width: '230.5px',
                height: '212px',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                zIndex: 2,
              }}
            >
              <Box
                component="img"
                src={(() => {
                  const baseUrl = import.meta.env.VITE_APP_ENDPOINT || 'http://localhost:5000';
                  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
                  const cleanPhotoPath = student.photo.startsWith('/') ? student.photo : `/${student.photo}`;
                  return `${cleanBaseUrl}${cleanPhotoPath}`;
                })()}
                alt="Student Photo"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'fill',
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </Box>
          )}
          
          {/* Subjects Table */}
          {subjectsWithCodes.length > 0 && (
            <>
              {subjectsWithCodes.map((subject: { subjectName: string; subjectCode: string }, index: number) => {
                // Starting position for subjects table - adjust based on actual SVG template
                const rowHeight = 43; // Height between rows
                const rowTop = 851.5 + (2 + index * rowHeight);
                
                return (
                  <Fragment key={index}>
                    {/* Subject Code */}
                    <Box sx={{ 
                      position: 'absolute', 
                      left: '74px', 
                      top: `${rowTop}px`,
                      height: `${rowHeight}px`,
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '1.2rem' }}>
                        {subject.subjectCode || 'N/A'}
                      </Typography>
                    </Box>
                    
                    {/* Subject Name */}
                    <Box sx={{ 
                      position: 'absolute', 
                      left: '190px', 
                      top: `${rowTop}px`, 
                      height: `${rowHeight}px`,
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '1.2rem' }}>
                        {subject.subjectName}
                      </Typography>
                    </Box>
                    
                    {/* Date of Exam */}
                    <Box sx={{ 
                      position: 'absolute', 
                      left: '915px', 
                      top: `${rowTop}px`,
                      height: `${rowHeight}px`,
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '1.2rem' }}>
                        TBA
                      </Typography>
                    </Box>
                  </Fragment>
                );
              })}
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ViewAdmitCardPage;
